import React, { useEffect, useState, useRef } from 'react';
import { LOGO_ASSETS } from '../utils/assets';
import '../styles/clients-section.css';

interface ClientLogo {
  src: string;
  alt: string;
}

const ALL_CLIENTS: ClientLogo[] = [
  { src: LOGO_ASSETS.channels42, alt: '42 Channels Logo' },
  { src: LOGO_ASSETS.m8werk, alt: 'M8Werk Group Logo' },
  { src: LOGO_ASSETS.presseaugsburg, alt: 'PresseAugsburg Logo' },
  { src: LOGO_ASSETS.serviceunion, alt: 'ServiceUnion Logo' },
  { src: LOGO_ASSETS.mohrbach, alt: 'Mohrbach Verpackungsmaschinen Logo' },
  { src: LOGO_ASSETS.allsafe, alt: 'allsafe Logo' },
  { src: LOGO_ASSETS.osnalive, alt: 'osna.live Logo' },
  { src: LOGO_ASSETS.lernkarten, alt: 'HFW Lernkarten Logo' },
  { src: LOGO_ASSETS.nextserve, alt: 'NextServe Logo' },
];

const ClientsSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pathD, setPathD] = useState('');
  const [areaPathD, setAreaPathD] = useState('');
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updatePath = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        const isMobile = window.innerWidth <= 768;
        const currentScale = isMobile ? 0.5 : 1;
        setScale(currentScale);
        
        // Desktop values * scale
        const startY = 300 * currentScale;
        const controlY = 150 * currentScale;
        const fillBottom = 200 * currentScale;

        // Curve: Start left, arc up, end right.
        // Shifted down to allow room for upward glow
        // M -150,300 (start below/left)
        // Q w/2,150 (control point top middle)
        // w+150,300 (end below/right)
        const curve = `M -150,${startY} Q ${w/2},${controlY} ${w+150},${startY}`;
        setPathD(curve);
        // Create a closed path for the "planet body" fill
        // Go down to 200px (just below view) to keep the gradient tight to the line
        setAreaPathD(`${curve} V ${fillBottom} H -100 Z`);
      }
    };

    updatePath();
    window.addEventListener('resize', updatePath);
    return () => window.removeEventListener('resize', updatePath);
  }, []);

  return (
    <section className="clients-section" ref={containerRef}>
      <div className="clients-header">
         <h2 className="section-title">Unternehmen, die uns vertrauen</h2>
      </div>
      
      <div className="curve-container">
        <svg className="curve-svg" width="100%" height="100%">
          <defs>
            <linearGradient id="atmosphereFill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(119, 247, 108, 0.5)" />
              <stop offset="60%" stopColor="rgba(119, 247, 108, 0.1)" />
              <stop offset="100%" stopColor="rgba(119, 247, 108, 0)" />
            </linearGradient>
            
            {/* Strong Blur Filter for the Haze */}
            <filter id="strongBlur" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={30 * scale} />
            </filter>

            {/* Medium Blur Filter for the Core Glow */}
            <filter id="mediumBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={8 * scale} />
            </filter>
            
            {/* Soft Blur for the Fill Body */}
            <filter id="bodyBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={15 * scale} />
            </filter>
          </defs>
          
          {/* 1. Atmosphere Body (Filled Gradient) - The "Planet" volume */}
          <path 
            d={areaPathD} 
            fill="url(#atmosphereFill)" 
            stroke="none"
            filter="url(#bodyBlur)"
            style={{ opacity: 0.7, transform: `translateY(${10 * scale}px)` }}
          />

          {/* 2. The Haze (Blurred Stroke) - Creates the outer glow */}
          <path 
            d={pathD} 
            fill="none" 
            stroke="rgba(119, 247, 108, 0.6)" 
            strokeWidth={60 * scale}
            filter="url(#strongBlur)"
            style={{ 
                opacity: 0.6,
                transform: `translateY(${20 * scale}px)`
            }}
          />

          {/* 3. Intense Core Glow */}
          <path 
            d={pathD} 
            fill="none" 
            stroke="#77F76C" 
            strokeWidth={8 * scale}
            filter="url(#mediumBlur)"
            style={{ 
                opacity: 0.9
            }}
          />

          {/* 4. The White Line (Sharp Horizon) */}
          <path 
            d={pathD} 
            fill="none" 
            stroke="#FFFFFF" 
            strokeWidth={2 * scale}
            style={{ 
                filter: 'drop-shadow(0 0 8px rgba(119, 247, 108, 0.9))',
                opacity: 1
            }}
          />
        </svg>

        {/* Logos moving along the path */}
        <div className="logos-track">
            {ALL_CLIENTS.map((client, index) => (
            <div 
                key={index}
                className="moving-logo"
                style={{
                    offsetPath: `path('${pathD}')`,
                    animationDelay: `${index * -4}s`, // Slower animation, more spacing
                } as React.CSSProperties}
            >
                <img src={client.src} alt={client.alt} />
            </div>
            ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
